import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import Grid from '@mui/material/Grid';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import {
  API,
  MimeTypeHelper,
  MediaFormatHelper,
  Field2 as Field,
  VibeIcon,
  viEdit,
  viAdd,
  viAddCircle,
  color,
  VibeCheckbox,
  VibeModal,
} from 'vibeguide';
import defaultLocationImage from '../../../assets/default_location.png';
import './Information.scss';

class Information extends PureComponent {
  constructor(props) {
    super(props);

    this.locationZone = null;

    this.state = {
      showOnToggleModal: false,
      showOffToggleModal: false,
      localTestLocation: false,
      banner: {},
    };
  }

  componentDidUpdate(prevProps) {
    const {
      bannerId,
      companyId,
    } = this.props;

    const {
      bannerId: prevBannerId,
    } = prevProps;

    // only fetch banner if a new one is selected (or first selected)
    if (!bannerId || !companyId || bannerId === prevBannerId) {
      return;
    }

    this.getBanner();
  }

  /**
   * Get Company Banner
   */
  getBanner = async () => {
    const {
      companyId,
      bannerId,
    } = this.props;

    const response = await API.Company.Banner.get({
      _id: bannerId,
      companyId,
    });

    this.setState({
      banner: response,
    });
  };

  /**
   * When a field is changed
   */
  onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const {
      onUpdate,
    } = this.props;

    const includeData = {};

    // when media format is changed (and a location zone is selected) choose the right impression multiplier to use
    if (name === 'mediaFormat' && this.locationZone) {
      const useImpressionMultiplier = get(this.locationZone, `${value}Multiplier`) > 0
        ? get(this.locationZone, `${value}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = this.locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * When the impression multiplier is changed
   */
  onChangeImpressionMultiplier = (e) => {
    const {
      onUpdate,
    } = this.props;

    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: toNumber(value) / 100,
    });
  };

  /**
   * When a dropdown is changed
   */
  onChangeDropdown = ({
    id,
    name,
    value,
    data,
  }) => {
    const {
      mediaFormat,
      onUpdate,
    } = this.props;

    const includeData = {};

    if (name === 'zoneName') {
      // include zone ID if changing the location zone
      includeData.zoneId = id;

      // save location zone
      this.locationZone = data;

      const useImpressionMultiplier = get(this.locationZone, `${mediaFormat}Multiplier`) > 0
        ? get(this.locationZone, `${mediaFormat}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = this.locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    } else if (name === 'bannerName') {
      // include banner ID if changing the location banner
      includeData.bannerId = id;

      // remove the location zone / impression multiplier when the banner is changed
      includeData.zoneId = '';
      includeData.zoneName = '';
      includeData.impressionMultiplier = 0;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * When the tag field is changed
   */
  onChangeTag = (data) => {
    const {
      onUpdate,
    } = this.props;

    onUpdate(data);
  };

  /**
   * When the company field is changed
   */
  onSetCompany = async ({
    id: companyId,
    value: companyName,
  }) => {
    const {
      companyId: currCompanyId,
      category,
      tags,
      user,
      onUpdate,
    } = this.props;

    const data = !companyId
      ? {
        tags: currCompanyId !== companyId
          ? tags.filter(tag => tag.type === 'admin')
          : tags,
      }
      : {};

    onUpdate({
      companyId,
      companyName,
      ...data,
    });

    // populate the category if not already set
    if (!category && user.can('company.view')) {
      const company = await API.Company.getById(companyId);

      onUpdate({
        category: company.category || '',
      });
    }
  };

  onDrop = (acceptedFiles) => {
    const file = get(acceptedFiles, '[0]', false);

    if (file) {
      const {
        onUpdate,
        onUploadImage,
      } = this.props;

      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        // Save image data to state to show as a preview
        onUpdate({
          imageUrl: fileAsDataURL,
        });
      };

      reader.onabort = () => console.error('file reading was aborted');
      reader.onerror = () => console.error('file reading has failed');

      reader.readAsDataURL(file);

      onUploadImage(file);
    }
  };

  confirmToggleTestLocation = () => {
    const {
      onUpdate,
    } = this.props;

    const {
      localTestLocation,
      showOffToggleModal,
    } = this.state;

    if (showOffToggleModal) {
      this.setState({
        showOffToggleModal: false,
      });
    } else {
      this.setState({
        showOnToggleModal: false,
      });
    }

    onUpdate({
      testLocation: localTestLocation,
    });
  };

  closeOnToggleTestLocationModal = () => {
    this.setState({
      showOnToggleModal: false,
    });
  };

  closeOffToggleTestLocationModal = () => {
    this.setState({
      showOffToggleModal: false,
    });
  };

  onToggleTestLocation = (e) => {
    const {
      checked,
    } = e;

    const {
      localTestLocation,
    } = this.state;

    if (!localTestLocation) {
      this.setState({
        showOnToggleModal: true,
        localTestLocation: checked,
      });
    } else {
      this.setState({
        showOffToggleModal: true,
        localTestLocation: checked,
      });
    }
  };

  render() {
    const {
      name,
      image: locationImage,
      companyName,
      companyId,
      bannerId,
      bannerName,
      category,
      salesforceId,
      testLocation,
      mediaFormat,
      zoneId,
      zoneName,
      impressionMultiplier,
      contentRating,
      tags,
      disableInput,
      user,
      isNew,
      disableBannerField,
    } = this.props;

    const {
      showOnToggleModal,
      showOffToggleModal,
      banner,
    } = this.state;

    const canAdminEdit = user.sysAdmin && !disableInput;
    const canSetCompany = user.sysAdmin;
    const showContentRating = mediaFormat !== 'visual';

    return (
      <div className="Information location">
        <div className="data-row">
          <div className="location-image">
            <Dropzone
              accept={MimeTypeHelper.images}
              multiple={false}
              onDrop={this.onDrop}
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                return (
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'dropzone--isActive' : ''}`}
                  >
                    <input {...getInputProps()} />

                    {locationImage ? (
                      <img src={locationImage} alt="Location Preview" />
                    ) : (
                      <img src={defaultLocationImage} alt="Location" />
                    )}

                    {isDragActive ? (
                      <div className="icon-drop">
                        <VibeIcon
                          className="icon"
                          icon={viAddCircle}
                          color={color.white}
                          size={40}
                        />
                      </div>
                    ) : null}

                    <div className="icon-container">
                      {isDragActive ? (
                        <VibeIcon
                          type="button"
                          icon={viAdd}
                          color={color.white}
                          size={16}
                          buttonProps={{
                            size: 24,
                            color: color.primary,
                          }}
                        />
                      ) : (
                        <VibeIcon
                          type="button"
                          icon={viEdit}
                          color={color.white}
                          size={16}
                          buttonProps={{
                            size: 24,
                            color: color.primary,
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              }}
            </Dropzone>
          </div>

          <div>
            <Grid spacing={2} container>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="text"
                  label="Name"
                  name="name"
                  placeholder="Name"
                  value={name}
                  tabIndex={20}
                  onChange={this.onChange}
                  disabled={disableInput}
                  required
                  autoFocus
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="company"
                  label="Company"
                  name="companyName"
                  value={companyName}
                  tabIndex={21}
                  companyProps={{
                    companyId,
                    companyName,
                  }}
                  disabled={disableInput || !canSetCompany || !isNew}
                  onChange={this.onSetCompany}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="dropdown"
                  label="Category"
                  name="category"
                  value={category}
                  tabIndex={22}
                  dropdownProps={{
                    type: 'category',
                    attr: 'value',
                    fetch: API.Company.Category.list,
                    create: API.Company.Category.create,
                    canCreate: user.can('company_category.create'),
                    successMessage: 'COMPANYCATEGORY.CREATED',
                  }}
                  disabled={disableInput}
                  onChange={this.onChangeDropdown}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="dropdown"
                  label="Location Banner"
                  name="bannerName"
                  value={bannerName}
                  tabIndex={23}
                  dropdownProps={{
                    type: 'banner',
                    attr: 'value',
                    companyId,
                    fetch: API.Company.Banner.list,
                    create: API.Company.Banner.create,
                    canCreate: user.can('company_banner.create'),
                    successMessage: 'COMPANYBANNER.CREATED',
                  }}
                  disabled={!canAdminEdit || disableBannerField || !companyId}
                  onChange={this.onChangeDropdown}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="text"
                  label="Salesforce ID"
                  name="salesforceId"
                  placeholder="XXXXXXXXXXXXXXXXXX"
                  value={salesforceId}
                  tabIndex={24}
                  disabled={!canAdminEdit}
                  onChange={this.onChange}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }} sx={{ '& .toggle-container': { marginTop: { lg: '20px' } } }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  className="toggle-container"
                >
                  <VibeCheckbox
                    label="Test Location"
                    name="testLocation"
                    color={color.primary}
                    checked={testLocation}
                    onChange={this.onToggleTestLocation}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Grid spacing={2} container>
              <Grid size={6}>
                <Field
                  type="select"
                  label="Media Format"
                  name="mediaFormat"
                  value={mediaFormat}
                  options={MediaFormatHelper.list({ includeEmpty: true })}
                  disabled={!isNew || !canAdminEdit}
                  tabIndex={25}
                  onChange={this.onChange}
                  required={isNew}
                />
              </Grid>

              {showContentRating && (
                <Grid size={6}>
                  <Field
                    type="select"
                    label="Max Allowed Content Rating"
                    name="contentRating"
                    value={contentRating}
                    options={[
                      {
                        label: '',
                        value: '',
                      },
                      {
                        label: 'Radio Edit',
                        value: 'radio-edit',
                      },
                      {
                        label: 'G',
                        value: 'g',
                      },
                      {
                        label: 'Explicit',
                        value: 'explicit',
                      },
                    ]}
                    tabIndex={26}
                    disabled={disableInput}
                    required
                    onChange={this.onChange}
                  />
                </Grid>
              )}
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Grid spacing={2} container>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="dropdown"
                  label="Location Zone"
                  placeholder="Search for a Location Zone..."
                  name="zoneName"
                  value={zoneName}
                  dropdownProps={{
                    type: 'collection',
                    attr: 'name',
                    collection: banner.locationZones,
                  }}
                  disabled={!canAdminEdit || !bannerId || !mediaFormat}
                  onChange={this.onChangeDropdown}
                  tabIndex={27}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="number"
                  label="Impression Multiplier"
                  name="impressionMultiplier"
                  placeholder="Enter value between 1-100"
                  value={impressionMultiplier || ''}
                  tabIndex={28}
                  numberProps={{
                    min: 0,
                    max: 100,
                    inputGroupRight: '%',
                  }}
                  disabled={!canAdminEdit || !zoneId}
                  onChange={this.onChangeImpressionMultiplier}
                />
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Field
              type="tags"
              label="Tags"
              name="tags"
              tagProps={{
                companyId,
                tags,
                allowTypes: ['client', 'admin'],
                showAs: 'list',
                assign: user.can('location.assign_tag'),
              }}
              disabled={disableInput}
              onChange={this.onChangeTag}
              tabIndex={29}
            />
          </div>
        </div>

        <VibeModal
          show={showOnToggleModal}
          type="confirm"
          title="Test Location"
          text="You are marking this as a test location, excluding it from reporting."
          onConfirm={this.confirmToggleTestLocation}
          onClose={this.closeOnToggleTestLocationModal}
        />

        <VibeModal
          show={showOffToggleModal}
          type="confirm"
          title="Test Location"
          text="You are removing this as a test location. This location will now show in reporting."
          onConfirm={this.confirmToggleTestLocation}
          onClose={this.closeOffToggleTestLocationModal}
        />
      </div>
    );
  }
}

Information.propTypes = {
  name: PropTypes.string,
  companyName: PropTypes.string,
  companyId: PropTypes.string,
  bannerId: PropTypes.string,
  bannerName: PropTypes.string,
  category: PropTypes.string,
  testLocation: PropTypes.bool,
  mediaFormat: PropTypes.string,
  zoneId: PropTypes.string,
  zoneName: PropTypes.string,
  impressionMultiplier: PropTypes.number,
  contentRating: PropTypes.string,
  image: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.object),
  disableInput: PropTypes.bool,
  disableBannerField: PropTypes.bool,
  isNew: PropTypes.bool,
  onUploadImage: PropTypes.func,
  onUpdate: PropTypes.func,
};

Information.defaultProps = {
  name: '',
  companyName: '',
  companyId: '',
  bannerId: '',
  bannerName: '',
  category: '',
  testLocation: false,
  mediaFormat: '',
  zoneId: '',
  zoneName: '',
  impressionMultiplier: null,
  contentRating: '',
  image: null,
  tags: [],
  disableInput: false,
  disableBannerField: false,
  isNew: false,
  onUploadImage: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(Information);
